import { Check, Sparkles, ThumbsUp, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';

import { Button, Input, Label, cn } from '@workspace/shadcn';

import { FORMSPREE_ENDPOINT } from '../constants/contact.ts';
import { courseCategories } from '../constants/course-categories.ts';
import { useModals } from './modals.tsx';

function SelectableChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex cursor-pointer items-center justify-between rounded-md border p-2 text-start text-sm transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        selected
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-input bg-background',
      )}
    >
      <span>{label}</span>
      {selected && <Check size={16} className="text-primary shrink-0" />}
    </button>
  );
}

export function SuggestionModal() {
  const { openModal, close } = useModals();
  const isOpen = openModal === 'suggestion';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    suggestion: '',
  });

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, close]);

  const handleClose = () => {
    close();
    setSubmitted(false);
  };

  const toggleCategory = (name: string) => {
    setSelectedCategories((previous) => {
      const next = previous.includes(name)
        ? previous.filter((category) => category !== name)
        : [...previous, name];

      setSelectedCourses((courses) =>
        courses.filter((course) =>
          next.some((category) =>
            courseCategories
              .find((entry) => entry.name === category)
              ?.items.includes(course),
          ),
        ),
      );

      return next;
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedCategories.length === 0) return;

    const form = event.currentTarget;
    setIsSubmitting(true);
    form.target = 'suggestion-form-target';
    form.submit();

    setTimeout(() => {
      setFormData({ name: '', email: '', suggestion: '' });
      setSelectedCategories([]);
      setSelectedCourses([]);
      setIsSubmitting(false);
      setSubmitted(true);
      setTimeout(handleClose, 5000);
    }, 2250);
  };

  const availableCourses = courseCategories
    .filter((category) => selectedCategories.includes(category.name))
    .flatMap((category) => category.items);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1000] flex items-center justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-background relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg p-6 shadow-xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close modal"
              className="bg-background/70 text-muted-foreground hover:bg-background absolute end-4 top-4 rounded-full p-1 backdrop-blur-sm transition-colors"
            >
              <X className="size-4" />
            </button>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="py-10 text-center"
              >
                <div className="mb-6 flex justify-center">
                  <div className="relative">
                    <ThumbsUp size={64} className="text-primary" />
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        delay: 0.3,
                        type: 'spring',
                        stiffness: 500,
                        damping: 15,
                      }}
                      className="bg-primary absolute -end-2 -top-2 rounded-full p-1"
                    >
                      <Check size={20} className="text-white" />
                    </motion.div>
                  </div>
                </div>
                <h2 className="text-primary mb-4 text-3xl font-bold">
                  Thank You!
                </h2>
                <p className="mb-4 text-xl">
                  Your course suggestion has been submitted successfully.
                </p>
                <p className="text-muted-foreground">
                  We truly appreciate your input and are excited to consider
                  your idea for our upcoming courses.
                </p>
              </motion.div>
            ) : (
              <>
                <h2 className="mb-6 flex items-center gap-1 text-2xl font-semibold">
                  <Sparkles className="text-primary size-5" /> Suggest a Course
                </h2>

                <form
                  onSubmit={handleSubmit}
                  action={FORMSPREE_ENDPOINT}
                  method="POST"
                  encType="multipart/form-data"
                  className="space-y-4"
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="suggestion-name">Your Name</Label>
                      <Input
                        id="suggestion-name"
                        name="name"
                        value={formData.name}
                        onChange={(event) =>
                          setFormData({ ...formData, name: event.target.value })
                        }
                        required
                        placeholder="Enter your name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="suggestion-email">Your Email</Label>
                      <Input
                        id="suggestion-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={(event) =>
                          setFormData({
                            ...formData,
                            email: event.target.value,
                          })
                        }
                        required
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2 block">
                      Field of Interest (Select at least one)
                    </Label>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {courseCategories.map((category) => (
                        <SelectableChip
                          key={category.name}
                          label={category.name}
                          selected={selectedCategories.includes(category.name)}
                          onClick={() => toggleCategory(category.name)}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2 block">
                      Course Topic{' '}
                      <span className="text-muted-foreground">(Optional)</span>
                    </Label>
                    {availableCourses.length > 0 ? (
                      <div className="grid max-h-40 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
                        {availableCourses.map((course) => (
                          <SelectableChip
                            key={course}
                            label={course}
                            selected={selectedCourses.includes(course)}
                            onClick={() =>
                              setSelectedCourses((previous) =>
                                previous.includes(course)
                                  ? previous.filter((item) => item !== course)
                                  : [...previous, course],
                              )
                            }
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        Select a category to view available courses
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="suggestion-idea">
                      Another Course Idea{' '}
                      <span className="text-muted-foreground">(Optional)</span>
                    </Label>
                    <Input
                      id="suggestion-idea"
                      name="suggestion"
                      value={formData.suggestion}
                      onChange={(event) =>
                        setFormData({
                          ...formData,
                          suggestion: event.target.value,
                        })
                      }
                      placeholder="Share your course idea or a topic you would like to learn about"
                    />
                  </div>

                  <input
                    type="hidden"
                    name="selectedCategories"
                    value={selectedCategories.join(', ')}
                  />
                  <input
                    type="hidden"
                    name="selectedCourses"
                    value={selectedCourses.join(', ')}
                  />

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={
                      isSubmitting ||
                      selectedCategories.length === 0 ||
                      !formData.name ||
                      !formData.email
                    }
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Your Suggestion'}
                  </Button>
                </form>
              </>
            )}

            <iframe
              name="suggestion-form-target"
              title="Suggestion submission target"
              className="hidden"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
